(() => {
  'use strict';

  const SELECTOR = '[data-mmx-tabbed-recommendations]';
  const STORAGE_KEY = 'mmx_recently_viewed_products_v1';
  const instances = new WeakMap();

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  function themeRoot() {
    return (
      window.Shopify
      && window.Shopify.routes
      && window.Shopify.routes.root
    ) || '/';
  }

  function refreshThemeProductCards(scope) {
    try {
      if (
        window.theme
        && window.theme.ProductBlockManager
        && typeof window.theme.ProductBlockManager.loadImages === 'function'
      ) {
        window.theme.ProductBlockManager.loadImages(scope);
      }
    } catch (error) {
      console.warn('[MMX recommendations] Product image refresh failed.', error);
    }

    try {
      if (
        window.theme
        && typeof window.theme.initAnimateOnScroll === 'function'
      ) {
        window.theme.initAnimateOnScroll();
      }
    } catch (error) {
      console.warn('[MMX recommendations] Animation refresh failed.', error);
    }
  }

  class MMXCarousel {
    constructor(root, panel) {
      this.root = root;
      this.panel = panel;
      this.viewport = panel.querySelector('[data-mmx-viewport]');
      this.track = panel.querySelector('[data-mmx-track]');
      this.previous = panel.querySelector('[data-mmx-previous]');
      this.next = panel.querySelector('[data-mmx-next]');
      this.dots = panel.querySelector('[data-mmx-dots]');
      this.currentPage = 0;
      this.scrollFrame = null;
      this.resizeObserver = null;

      this.onPrevious = () => this.goToPage(this.currentPage - 1);
      this.onNext = () => this.goToPage(this.currentPage + 1);
      this.onScroll = () => {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.updateFromScroll();
        });
      };

      if (this.previous) {
        this.previous.addEventListener('click', this.onPrevious);
      }

      if (this.next) {
        this.next.addEventListener('click', this.onNext);
      }

      if (this.viewport) {
        this.viewport.addEventListener('scroll', this.onScroll, {
          passive: true
        });
      }

      if ('ResizeObserver' in window && this.viewport) {
        this.resizeObserver = new ResizeObserver(() => this.refresh());
        this.resizeObserver.observe(this.viewport);
      }

      this.refresh();
    }

    get items() {
      return this.track
        ? Array.from(this.track.querySelectorAll(':scope > .mmx-tpr__item'))
        : [];
    }

    perView() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const value = mobile
        ? Number(this.root.dataset.productsMobile || 0)
        : Number(this.root.dataset.productsDesktop || 0);

      if (value) return value;

      const className = Array.from(this.root.classList).find((name) => (
        name.startsWith(mobile ? 'mmx-tpr--mobile-' : 'mmx-tpr--desktop-')
      ));

      return clamp(
        Number(className && className.split('-').pop()) || (mobile ? 2 : 4),
        1,
        5
      );
    }

    pageCount() {
      return Math.max(1, Math.ceil(this.items.length / this.perView()));
    }

    refresh() {
      const count = this.pageCount();
      this.currentPage = clamp(this.currentPage, 0, count - 1);

      if (this.previous) {
        this.previous.hidden = count <= 1;
      }

      if (this.next) {
        this.next.hidden = count <= 1;
      }

      this.renderDots();
      this.updateControls();
    }

    renderDots() {
      if (!this.dots) return;

      const count = this.pageCount();

      if (count <= 1) {
        this.dots.innerHTML = '';
        this.dots.hidden = true;
        return;
      }

      this.dots.hidden = false;
      this.dots.innerHTML = Array.from({ length: count }, (_, index) => (
        `<button
          type="button"
          class="mmx-tpr__dot${index === this.currentPage ? ' is-active' : ''}"
          data-mmx-page="${index}"
          aria-label="Go to product page ${index + 1}"
          ${index === this.currentPage ? 'aria-current="true"' : ''}
        ></button>`
      )).join('');

      this.dots.querySelectorAll('[data-mmx-page]').forEach((button) => {
        button.addEventListener('click', () => {
          this.goToPage(Number(button.dataset.mmxPage || 0));
        });
      });
    }

    goToPage(page, immediate = false) {
      const items = this.items;
      const pageCount = this.pageCount();
      const nextPage = clamp(Number(page || 0), 0, pageCount - 1);
      const targetIndex = Math.min(
        items.length - 1,
        nextPage * this.perView()
      );
      const target = items[targetIndex];

      this.currentPage = nextPage;
      this.updateControls();

      if (!target || !this.viewport) return;

      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.viewport.scrollTo({
        left: target.offsetLeft,
        behavior: immediate || reducedMotion ? 'auto' : 'smooth'
      });
    }

    updateFromScroll() {
      if (!this.viewport || !this.items.length) return;

      const left = this.viewport.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      this.items.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft - left);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      const page = clamp(
        Math.round(nearestIndex / this.perView()),
        0,
        this.pageCount() - 1
      );

      if (page !== this.currentPage) {
        this.currentPage = page;
        this.updateControls();
      }
    }

    updateControls() {
      const count = this.pageCount();

      if (this.previous) {
        this.previous.disabled = this.currentPage <= 0;
      }

      if (this.next) {
        this.next.disabled = this.currentPage >= count - 1;
      }

      if (this.dots) {
        this.dots.querySelectorAll('[data-mmx-page]').forEach((dot) => {
          const active = Number(dot.dataset.mmxPage || 0) === this.currentPage;
          dot.classList.toggle('is-active', active);

          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }

    destroy() {
      window.cancelAnimationFrame(this.scrollFrame);

      if (this.previous) {
        this.previous.removeEventListener('click', this.onPrevious);
      }

      if (this.next) {
        this.next.removeEventListener('click', this.onNext);
      }

      if (this.viewport) {
        this.viewport.removeEventListener('scroll', this.onScroll);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }
  }

  class MMXTabbedRecommendations {
    constructor(root) {
      this.root = root;
      this.tabs = Array.from(root.querySelectorAll('[data-mmx-tab]'));
      this.panels = new Map(
        Array.from(root.querySelectorAll('[data-mmx-panel]')).map((panel) => (
          [panel.dataset.mmxPanel, panel]
        ))
      );
      this.carousels = new Map();
      this.relatedRequested = false;
      this.recentRequested = false;
      this.destroyed = false;
      this.observer = null;

      this.currentProduct = {
        id: Number(root.dataset.currentProductId || 0),
        handle: String(root.dataset.currentProductHandle || '').trim(),
        url: String(root.dataset.currentProductUrl || '').trim()
      };

      this.maxRecent = clamp(Number(root.dataset.maxRecent || 8), 1, 24);
      this.retentionDays = clamp(
        Number(root.dataset.retentionDays || 60),
        1,
        365
      );
      this.hideEmptyRecent = root.dataset.hideEmptyRecent === 'true';

      this.onTabClick = (event) => {
        const tab = event.currentTarget;
        this.activateTab(tab.dataset.mmxTab, true);
      };

      this.onTabKeydown = (event) => {
        const currentIndex = this.tabs.indexOf(event.currentTarget);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') nextIndex += 1;
        if (event.key === 'ArrowLeft') nextIndex -= 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = this.tabs.length - 1;

        if (nextIndex === currentIndex) return;

        event.preventDefault();
        nextIndex = (nextIndex + this.tabs.length) % this.tabs.length;

        const nextTab = this.tabs[nextIndex];
        if (!nextTab || nextTab.hidden) return;

        this.activateTab(nextTab.dataset.mmxTab, true);
      };

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onTabKeydown);
      });

      this.recordCurrentProduct();
      this.updateRecentTabAvailability();

      const defaultTab = root.dataset.defaultTab === 'recent'
        ? 'recent'
        : 'related';

      const availableDefault = (
        defaultTab === 'recent'
        && this.recentEntries().length === 0
        && this.hideEmptyRecent
      ) ? 'related' : defaultTab;

      this.activateTab(availableDefault, false);
      this.deferRelatedProducts();
    }

    safeHistoryRead() {
      try {
        const parsed = JSON.parse(
          window.localStorage.getItem(STORAGE_KEY) || '[]'
        );

        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    safeHistoryWrite(entries) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
      } catch (error) {
        // Private browsing or storage restrictions should not break the section.
      }
    }

    cleanHistory(entries) {
      const expiry = Date.now() - (
        this.retentionDays * 24 * 60 * 60 * 1000
      );
      const seen = new Set();

      return (Array.isArray(entries) ? entries : [])
        .filter((entry) => {
          const id = Number(entry && entry.id || 0);
          const handle = String(entry && entry.handle || '').trim();
          const viewedAt = Number(entry && entry.viewedAt || 0);

          if ((!id && !handle) || viewedAt < expiry) return false;

          const key = id
            ? `id:${id}`
            : `handle:${handle.toLowerCase()}`;

          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, 24);
    }

    recordCurrentProduct() {
      const { id, handle, url } = this.currentProduct;
      if (!id || !handle) return;

      const existing = this.cleanHistory(this.safeHistoryRead())
        .filter((entry) => (
          Number(entry.id || 0) !== id
          && String(entry.handle || '').toLowerCase() !== handle.toLowerCase()
        ));

      this.safeHistoryWrite([
        {
          id,
          handle,
          url: url || `${themeRoot()}products/${encodeURIComponent(handle)}`,
          viewedAt: Date.now()
        },
        ...existing
      ].slice(0, 24));
    }

    recentEntries() {
      const currentHandle = this.currentProduct.handle.toLowerCase();
      const currentId = this.currentProduct.id;

      const entries = this.cleanHistory(this.safeHistoryRead())
        .filter((entry) => (
          Number(entry.id || 0) !== currentId
          && String(entry.handle || '').toLowerCase() !== currentHandle
        ))
        .slice(0, this.maxRecent);

      return entries;
    }

    updateRecentTabAvailability() {
      const tab = this.tabs.find((item) => item.dataset.mmxTab === 'recent');
      const hasEntries = this.recentEntries().length > 0;

      if (tab && this.hideEmptyRecent) {
        tab.hidden = !hasEntries;
      }
    }

    activateTab(name, focus) {
      const requested = this.panels.has(name) ? name : 'related';
      const panel = this.panels.get(requested);
      const tab = this.tabs.find((item) => item.dataset.mmxTab === requested);

      if (!panel || !tab || tab.hidden) return;

      this.tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((item, key) => {
        item.hidden = key !== requested;
      });

      if (focus) tab.focus();

      if (requested === 'related') {
        this.loadRelatedProducts();
      } else {
        this.loadRecentlyViewed();
      }

      window.requestAnimationFrame(() => {
        const carousel = this.carousels.get(requested);
        if (carousel) carousel.refresh();
      });
    }

    deferRelatedProducts() {
      const panel = this.panels.get('related');
      if (!panel || panel.dataset.loaded === 'true') {
        this.finishPanel('related');
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.loadRelatedProducts();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadRelatedProducts();
          this.observer.disconnect();
          this.observer = null;
        }
      }, {
        rootMargin: '350px 0px'
      });

      this.observer.observe(this.root);
    }

    async loadRelatedProducts() {
      const panel = this.panels.get('related');

      if (
        !panel
        || panel.dataset.loaded === 'true'
        || this.relatedRequested
      ) {
        if (panel && panel.dataset.loaded === 'true') {
          this.finishPanel('related');
        }
        return;
      }

      this.relatedRequested = true;

      try {
        const response = await fetch(this.root.dataset.relatedUrl, {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html'
          }
        });

        if (!response.ok) {
          throw new Error(`Related products failed: ${response.status}`);
        }

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(
          html,
          'text/html'
        );
        const responsePanel = documentFragment.querySelector(
          '[data-mmx-panel="related"]'
        );

        if (!responsePanel) {
          throw new Error('Related products panel was not returned.');
        }

        panel.innerHTML = responsePanel.innerHTML;
        panel.dataset.loaded = 'true';
        this.finishPanel('related');
      } catch (error) {
        console.warn('[MMX recommendations] Related products failed.', error);
        this.setPanelStatus(
          panel,
          panel.querySelector('[data-mmx-status]')?.textContent
            || 'No related products are available.'
        );
        panel.dataset.loaded = 'true';
        this.finishPanel('related');
      }
    }

    async loadRecentlyViewed() {
      const panel = this.panels.get('recent');

      if (
        !panel
        || panel.dataset.loaded === 'true'
        || this.recentRequested
      ) {
        if (panel && panel.dataset.loaded === 'true') {
          this.finishPanel('recent');
        }
        return;
      }

      this.recentRequested = true;
      const entries = this.recentEntries();

      if (!entries.length) {
        panel.dataset.loaded = 'true';
        this.showEmptyRecent(panel);
        return;
      }

      const track = panel.querySelector('[data-mmx-track]');
      const status = panel.querySelector('[data-mmx-status]');

      if (status) {
        status.hidden = false;
      }

      const cards = await this.mapWithConcurrency(
        entries,
        3,
        (entry) => this.fetchRecentCard(entry)
      );

      if (this.destroyed) return;

      const validCards = cards.filter(Boolean);

      if (track) {
        track.innerHTML = validCards.join('');
      }

      panel.dataset.loaded = 'true';

      if (!validCards.length) {
        this.showEmptyRecent(panel);
        return;
      }

      if (status) status.hidden = true;
      this.finishPanel('recent');
    }

    async fetchRecentCard(entry) {
      const handle = String(entry && entry.handle || '').trim();
      if (!handle) return '';

      try {
        const productUrl = new URL(
          String(entry.url || `${themeRoot()}products/${encodeURIComponent(handle)}`),
          window.location.origin
        );

        if (productUrl.origin !== window.location.origin) return '';

        productUrl.searchParams.set(
          'section_id',
          'mmx-recent-product-card'
        );

        const response = await fetch(productUrl.toString(), {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html'
          }
        });

        if (!response.ok) return '';

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const card = parsed.querySelector('.mmx-tpr__item');

        return card ? card.outerHTML : '';
      } catch (error) {
        console.warn(
          `[MMX recommendations] Recently viewed product failed: ${handle}`,
          error
        );
        return '';
      }
    }

    async mapWithConcurrency(items, concurrency, worker) {
      const results = new Array(items.length);
      let cursor = 0;

      const runners = Array.from(
        { length: Math.min(concurrency, items.length) },
        async () => {
          while (cursor < items.length) {
            const index = cursor;
            cursor += 1;
            results[index] = await worker(items[index], index);
          }
        }
      );

      await Promise.all(runners);
      return results;
    }

    showEmptyRecent(panel) {
      const tab = this.tabs.find((item) => item.dataset.mmxTab === 'recent');

      if (this.hideEmptyRecent && tab) {
        tab.hidden = true;

        if (tab.getAttribute('aria-selected') === 'true') {
          this.activateTab('related', false);
        }
      }

      const status = panel.querySelector('[data-mmx-status]');
      if (status) {
        status.hidden = false;
        status.textContent = (
          this.root.querySelector('[data-mmx-panel="recent"] [data-mmx-status]')
          ?.textContent
          || 'Your recently viewed products will appear here.'
        );
      }

      this.maybeHideSection();
    }

    setPanelStatus(panel, text) {
      if (!panel) return;
      const status = panel.querySelector('[data-mmx-status]');
      if (!status) return;
      status.hidden = false;
      status.textContent = text;
    }

    finishPanel(name) {
      const panel = this.panels.get(name);
      if (!panel) return;

      const items = panel.querySelectorAll('.mmx-tpr__item');
      const status = panel.querySelector('[data-mmx-status]');

      if (status) {
        status.hidden = items.length > 0;
      }

      const previous = this.carousels.get(name);
      if (previous) previous.destroy();

      if (items.length > 0) {
        this.carousels.set(name, new MMXCarousel(this.root, panel));
        refreshThemeProductCards(panel);
      } else {
        this.carousels.delete(name);
      }

      this.maybeHideSection();
    }

    maybeHideSection() {
      const related = this.panels.get('related');
      const recent = this.panels.get('recent');
      const relatedLoaded = related && related.dataset.loaded === 'true';
      const recentLoaded = recent && recent.dataset.loaded === 'true';
      const relatedCount = related
        ? related.querySelectorAll('.mmx-tpr__item').length
        : 0;
      const recentCount = recent
        ? recent.querySelectorAll('.mmx-tpr__item').length
        : 0;

      if (relatedLoaded && recentLoaded && !relatedCount && !recentCount) {
        this.root.hidden = true;
      }
    }

    destroy() {
      this.destroyed = true;

      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onTabKeydown);
      });

      this.carousels.forEach((carousel) => carousel.destroy());
      this.carousels.clear();

      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  function initialise(scope = document) {
    const roots = [];

    if (scope.matches && scope.matches(SELECTOR)) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(...scope.querySelectorAll(SELECTOR));
    }

    roots.forEach((root) => {
      if (instances.has(root)) return;
      const instance = new MMXTabbedRecommendations(root);
      instances.set(root, instance);
    });
  }

  function destroy(scope) {
    if (!scope || !scope.querySelectorAll) return;

    const roots = [];

    if (scope.matches && scope.matches(SELECTOR)) {
      roots.push(scope);
    }

    roots.push(...scope.querySelectorAll(SELECTOR));

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialise(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;

    document.querySelectorAll(SELECTOR).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;

      const panel = instance.panels.get('recent');
      instance.recentRequested = false;

      if (panel) {
        panel.dataset.loaded = 'false';
        const track = panel.querySelector('[data-mmx-track]');
        if (track) track.innerHTML = '';
      }

      instance.updateRecentTabAvailability();
    });
  });
})();
